/**
 * The web's way into the admin portal window (KPR-309; moved here from
 * utils/adminPortal.js by KPR-1668 so that `window.open` lives only in
 * src/platform). utils/adminPortal.js keeps the exports the app imports and
 * delegates to `platform.openAdmin`; in the phone app that is the same
 * WebView, because a phone has no second window.
 *
 * The reasoning for a named window, and for never `rel="noopener"`, is in
 * utils/adminPortal.js.
 */

/** The admin tab's window name. */
export const ADMIN_WINDOW_NAME = 'keepr-admin';

/** A click the browser should handle itself (new tab, new window, download). */
function isModifiedClick(event) {
	if (!event) return false;
	return Boolean(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || (typeof event.button === 'number' && event.button !== 0));
}

/** Whether a window we hold is still the blank page window.open just made. */
function isBlank(win) {
	try {
		const href = win.location.href;
		return !href || href === 'about:blank';
	} catch {
		// Not readable: the tab has gone somewhere else entirely. Point it back.
		return true;
	}
}

/**
 * Opens the portal at `path` in the admin tab, or brings that tab forward.
 *
 * With an `event` (a click on the rail's anchor), a modified or non-primary
 * click is left to the browser and nothing else happens. Returns the window
 * the portal is in, or null when the browser refused a new window — then the
 * portal opens in this tab instead, so the admin is never stuck.
 */
export function openAdminWindow(path, event, win = window) {
	if (isModifiedClick(event)) return null;
	if (event && typeof event.preventDefault === 'function') event.preventDefault();

	// THIS tab is the admin tab (the admin left the portal with Back to keepr
	// and is now in the app here): window.open would hand back this very
	// window and nothing would happen, so go to the portal in place.
	if (win.name === ADMIN_WINDOW_NAME) {
		win.location.assign(path);
		return win;
	}

	let target = null;
	try {
		target = win.open('', ADMIN_WINDOW_NAME);
	} catch {
		target = null;
	}
	if (!target) {
		// A popup blocker said no: open it here rather than not at all.
		win.location.assign(path);
		return null;
	}
	if (isBlank(target)) {
		target.location.href = path;
	}
	try { target.focus(); } catch { /* best effort: the browser decides focus */ }
	return target;
}
